import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, CircularProgress, IconButton, Box, FormControlLabel, Checkbox } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { updateGroup } from '../../api/groups';
import { GroupInterface } from '../../types';
import RichTextEditor from '../RichTextEditor';

interface EditGroupModalProps {
  open: boolean;
  group: GroupInterface | null;
  onClose: () => void;
  onGroupUpdated: () => void;
}

function EditGroupModal({ open, group, onClose, onGroupUpdated }: EditGroupModalProps): React.JSX.Element {
  const [form, setForm] = useState({ name: '', order: '', details: '', active: true });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (group) {
      setForm({
        name: group.name || '',
        order: group.order?.toString() || '',
        details: group.details || '',
        active: group.active !== false // default to true if undefined
      });
    }
  }, [group]);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleCheckboxChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.checked }));
  };

  const handleEditorChange = (content: string): void => {
    setForm(prev => ({ ...prev, details: content }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!group) return;
    setLoading(true);
    try {
      await updateGroup(group.id, {
        name: form.name.trim(),
        order: parseInt(form.order) || 0,
        details: form.details,
        active: form.active
      });
      onGroupUpdated();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        ערוך קבוצה
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box component="form" id="edit-group-form" onSubmit={handleSubmit} sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="שם" value={form.name} onChange={handleChange('name')} required fullWidth />
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField label="מיקום במחירון" type="number" value={form.order} onChange={handleChange('order')} sx={{ flex: 1 }} />
            <FormControlLabel
              control={<Checkbox checked={form.active} onChange={handleCheckboxChange('active')} color="primary" />}
              label="פעיל"
            />
          </Box>
          <RichTextEditor content={form.details} onChange={handleEditorChange} placeholder="הזן פרטי קבוצה..." />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="secondary" disabled={loading}>ביטול</Button>
        <Button type="submit" form="edit-group-form" variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'שמור'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditGroupModal; 